import type { FC, PropsWithChildren } from "react";
import Markdown from "react-native-markdown-display";
import { Link } from "expo-router";
import { Button } from "@ptsd/uikit";
import { useTranslation } from "react-i18next";
import type { FormProps, ImageProps, ViewProps } from "tamagui";
import {
  Anchor,
  Form,
  Image,
  Separator,
  Text,
  useMedia,
  View,
  XStack,
} from "tamagui";

import { GuestErrorScreen } from "../guest/guest-error-screen";

export const AuthContainer: FC<ViewProps> = ({ children, $sm, ...props }) => {
  return (
    <View
      flex={2}
      width={"100%"}
      flexDirection="row"
      $sm={{ flex: 1, ...$sm }}
      {...props}
    >
      {children}
    </View>
  );
};

export const AuthContent: FC<ViewProps> = ({
  children,
  $sm,
  $gtSm,
  ...props
}) => {
  return (
    <View
      flex={1}
      width={"50%"}
      alignItems="flex-end"
      justifyContent="center"
      paddingHorizontal={"$2.5"}
      $sm={{ alignItems: "center", justifyContent: "flex-start", ...$sm }}
      $gtSm={{ paddingRight: 110, ...$gtSm }}
      {...props}
    >
      {children}
    </View>
  );
};

export interface AuthImageProps extends ImageProps {
  containerProps?: ViewProps;
}

export const AuthImage: FC<AuthImageProps> = ({ containerProps, ...props }) => {
  const { sm } = useMedia();
  if (sm) return false;

  return (
    <View
      flex={1}
      width={"50%"}
      alignItems="flex-start"
      justifyContent="center"
      paddingHorizontal={"$2.5"}
      paddingLeft={110}
      {...containerProps}
    >
      <Image
        maxHeight={460}
        maxWidth={500}
        contain="size"
        width="100%"
        resizeMode="contain"
        aspectRatio={1}
        {...props}
      />
    </View>
  );
};

export const AuthBaseForm: FC<FormProps> = ({ children, ...props }) => {
  return (
    <Form
      width={"100%"}
      maxWidth={413}
      gap={"$4"}
      justifyContent="flex-start"
      paddingVertical={"$4"}
      {...props}
    >
      {children}
    </Form>
  );
};

export const AuthSlugWrapper: FC<
  PropsWithChildren<{
    isSuccess?: boolean;
    isError?: boolean;
  }>
> = ({ isError, isSuccess, children }) => {
  const { t } = useTranslation("signup");

  if (isError) {
    return (
      <GuestErrorScreen text={t("ui.error.inactiveLink")} withBackBtn={true} />
    );
  }

  if (isSuccess) {
    return (
      <GuestErrorScreen
        withBackBtn={false}
        title={"Ми надіслали вам лист для підтвердження реєстрації."}
        text={
          "Будь ласка, перевірте свою електронну пошту та підтвердіть реєстрацію."
        }
      />
    );
  }
  return children;
};

export const AuthCodeWrapper: FC<
  PropsWithChildren<{
    isError: boolean;
    data: { code?: string; email?: string } | undefined;
  }>
> = ({ data, isError, children }) => {
  const { t } = useTranslation("signup");

  if (data?.code === "CONFLICT") {
    return (
      <View width={"100%"} gap={"$3"} maxWidth={413}>
        <View width={"100%"} gap={"$2"}>
          <Text
            fontFamily={"$subtitle"}
            fontWeight={"$6"}
            fontSize={"$7"}
            lineHeight={"$7"}
          >
            Щось пішло не так 🤕
          </Text>

          <Markdown
            style={{
              body: { fontFamily: "Fixel Text", fontSize: 18, lineHeight: 22 },
            }}
          >
            {t("ui.error.isRegistered")}
          </Markdown>
        </View>
        <Separator borderColor={"$gray-200"} />
        <XStack gap={"$1"}>
          <Link href={"/"} asChild>
            <Button
              variant="primary"
              width={"100%"}
              size="lg"
              rounded="lg"
              flex={1}
            >
              Перейти
            </Button>
          </Link>
        </XStack>
      </View>
    );
  }

  if (data?.code === "FORBIDDEN") {
    return (
      <View width={"100%"} gap={"$3"} maxWidth={413}>
        <View width={"100%"} gap={"$2"}>
          <Text
            fontFamily={"$subtitle"}
            fontWeight={"$6"}
            fontSize={"$7"}
            lineHeight={"$7"}
          >
            Щось пішло не так 🤕
          </Text>
        </View>
        <Separator borderColor={"$gray-200"} />
        <XStack gap={"$1"}>
          <Anchor
            href="mailto:service@healthymind.help?subject=Активація%20аккаунту"
            target="_blank"
            fontFamily={"$body"}
            fontSize={"$4"}
            lineHeight={"$4"}
            style={{ color: "blue", textDecorationLine: "underline" }}
          >
            Ваш акаунт деактивовано, зверніться до служби підтримки, щоб його
            активувати
          </Anchor>
        </XStack>
      </View>
    );
  }

  if (isError) {
    return (
      <View width={"100%"} gap={"$3"} maxWidth={413}>
        <View width={"100%"} gap={"$2"}>
          <Text
            fontFamily={"$subtitle"}
            fontWeight={"$6"}
            fontSize={"$7"}
            lineHeight={"$7"}
          >
            Щось пішло не так 🤕
          </Text>

          <Markdown
            style={{
              body: { fontFamily: "Fixel Text", fontSize: 18, lineHeight: 22 },
            }}
          >
            {t("ui.error.inactiveLink")}
          </Markdown>
        </View>
        <Separator borderColor={"$gray-200"} />
        <XStack gap={"$1"}>
          <Link href={"/"} asChild>
            <Button
              variant="primary"
              width={"100%"}
              size="lg"
              rounded="lg"
              flex={1}
            >
              На Головну
            </Button>
          </Link>
        </XStack>
      </View>
    );
  }

  return children;
};
